/**
 * Справочник нативных пользователей — `GET /v2/users` на core-бинаре.
 *
 * Не путать с legacy `GET /users` на основном бинаре: тот отдаёт зеркало
 * пользователей Bitrix с числовыми `ID` и живёт для старых экранов. Здесь —
 * UUID, на которые ссылаются `/v2/tasks` (`responsibleId`, `createdBy`,
 * `accomplices`, `auditors`).
 */
import { coreHttp } from '@/shared/api';

import type {
  NativeUserListItem,
  NativeUserUpdate,
  UserSkill,
} from '../model/types';

export type NativeUserStatus = 'active' | 'invited' | 'suspended' | 'all';

type NativeUserListResponse = {
  result?: NativeUserListItem[];
  total?: number;
};

/** По умолчанию бэк отдаёт только активных — приглашённые и отключённые скрыты. */
export async function fetchNativeUsers(
  status?: NativeUserStatus
): Promise<{ result: NativeUserListItem[]; total: number }> {
  const { data } = await coreHttp.get<NativeUserListResponse>('/v2/users', {
    params: status ? { status } : undefined,
  });

  return { result: data.result ?? [], total: data.total ?? 0 };
}

export async function fetchNativeUser(id: string): Promise<NativeUserListItem> {
  const { data } = await coreHttp.get<NativeUserListItem>(
    `/v2/users/${encodeURIComponent(id)}`
  );

  return data;
}

/**
 * Правка профиля. Бэк пускает только к себе: чужой id отдаёт 403 (проверено
 * на живом стенде), поэтому кнопка сохранения видна лишь на своей карточке.
 */
export async function updateNativeUser(
  id: string,
  payload: NativeUserUpdate
): Promise<NativeUserListItem> {
  const { data } = await coreHttp.patch<NativeUserListItem>(
    `/v2/users/${encodeURIComponent(id)}`,
    payload
  );

  return data;
}

export async function fetchUserSkills(id: string): Promise<UserSkill[]> {
  const { data } = await coreHttp.get<{ result?: UserSkill[] }>(
    `/v2/users/${encodeURIComponent(id)}/skills`
  );

  return Array.isArray(data?.result) ? data.result : [];
}

/** PUT, а не PATCH: список навыков заменяется целиком. */
export async function replaceUserSkills(
  id: string,
  skills: UserSkill[]
): Promise<UserSkill[]> {
  const { data } = await coreHttp.put<{ result?: UserSkill[] }>(
    `/v2/users/${encodeURIComponent(id)}/skills`,
    { skills }
  );

  return Array.isArray(data?.result) ? data.result : [];
}
